/**
 * Virtual accounts API (TMMAPP-0004, extended by TMMAPP-0005 / TMMAPP-0011) — wire types from
 * `src/api/generated/`; every response is normalized into the module's domain type.
 * `DELETE /v1/virtual-accounts/{id}` is never called (retired by the archive / restore contract, MM-D-62).
 */
import { download, request, type DownloadResult } from './client';
import type {
  VirtualAccountCreateRequest,
  VirtualAccountPatchRequest,
  Detail as WireDetail,
  ListItem as WireListItem,
} from './generated';
import { normalizeStanding, type MemberStanding } from './memberStanding';
import { personNameOrNull } from './personNames';
import { normalizeShares, type Share } from './shares';
import { normalizeEnvelopes, type Envelope } from './envelopes';

export type VirtualAccountStatus = 'ACTIVE' | 'ARCHIVED';

export interface VirtualAccountListItem extends MemberStanding {
  id: number;
  name: string;
  /** The owner's display name; `null` when they have none (render with `personName`). */
  ownerDisplayName: string | null;
  /**
   * The owner's user id (TMMAPI-0142, required by the contract): a grouping and equality key only,
   * never rendered or sent.
   */
  ownerUserId: number;
  currency: string;
  mine: boolean;
  private: boolean;
  shareCount: number;
  envelopeCount: number;
  status: VirtualAccountStatus;
  archivedAt: string | null;
  /**
   * The headline (TMMAPI-0145, MM-D-112): Unassigned plus every envelope, archived ones included, in
   * the owner's currency. Null when not served (a LOCKED owner's row).
   */
  balanceMinor: number | null;
  /** The money in no envelope (MM-D-112); may be negative. Null when not served. Never derived. */
  unassignedMinor: number | null;
  /** The active envelopes' total. Null when not served. */
  envelopesMinor: number | null;
  /** The archived envelopes' total, so the headline adds up. Null when not served. */
  archivedEnvelopesMinor: number | null;
  /**
   * The people holding an ACTIVE share, by display name (TMMAPI-0186 U3). Served on the caller's
   * own virtual accounts only; `[]` when omitted (one shared with them, or none shared).
   */
  sharedWithNames: string[];
  /** The pending invitations, by name (TMMAPI-0186 U3); `[]` when omitted. */
  invitedNames: string[];
}

export interface VirtualAccountDetail extends MemberStanding {
  id: number;
  name: string;
  /** The owner's display name; `null` when they have none (render with `personName`). */
  ownerDisplayName: string | null;
  /**
   * The owner's user id (TMMAPI-0142, required by the contract): a grouping and equality key only,
   * never rendered or sent.
   */
  ownerUserId: number;
  /** The virtual account OWNER's currency — every amount inside renders in it (§14.1), never the viewer's. */
  currency: string;
  mine: boolean;
  private: boolean;
  shareCount: number;
  shares: Share[];
  envelopes: Envelope[];
  status: VirtualAccountStatus;
  archivedAt: string | null;
  /** The headline: Unassigned + envelopes + archived envelopes (TMMAPI-0145). */
  balanceMinor: number;
  /** The money in no envelope (MM-D-112); may be negative. Never derived. */
  unassignedMinor: number;
  /** The active envelopes' total. */
  envelopesMinor: number;
  /** The archived envelopes' total. */
  archivedEnvelopesMinor: number;
}

export type CreateVirtualAccountRequest = VirtualAccountCreateRequest;
export type UpdateVirtualAccountRequest = VirtualAccountPatchRequest;

const status = (v: string): VirtualAccountStatus => (v === 'ARCHIVED' ? 'ARCHIVED' : 'ACTIVE');

function commonFields(w: WireListItem | WireDetail) {
  return {
    id: w.id,
    name: w.name,
    ownerDisplayName: personNameOrNull(w.ownerDisplayName),
    ownerUserId: w.ownerUserId,
    currency: w.currency ?? '',
    mine: w.mine,
    private: w.private,
    shareCount: w.shareCount,
    status: status(w.status),
    archivedAt: w.archivedAt ?? null,
    ...normalizeStanding(w),
  };
}

/** A list row. A LOCKED owner's row carries no figures (absent, never a zero). */
export function normalizeListItem(w: WireListItem): VirtualAccountListItem {
  return {
    ...commonFields(w),
    envelopeCount: w.envelopeCount,
    sharedWithNames: w.sharedWithNames ?? [],
    invitedNames: w.invitedNames ?? [],
    balanceMinor: w.balanceMinor ?? null,
    unassignedMinor: w.unassignedMinor ?? null,
    envelopesMinor: w.envelopesMinor ?? null,
    archivedEnvelopesMinor: w.archivedEnvelopesMinor ?? null,
  };
}

/** A detail. */
export function normalizeDetail(w: WireDetail): VirtualAccountDetail {
  return {
    ...commonFields(w),
    balanceMinor: w.balanceMinor,
    unassignedMinor: w.unassignedMinor,
    envelopesMinor: w.envelopesMinor,
    archivedEnvelopesMinor: w.archivedEnvelopesMinor,
    shares: normalizeShares(w.shares),
    envelopes: normalizeEnvelopes(w.envelopes),
  };
}

export const virtualAccountsApi = {
  /** GET /v1/virtual-accounts — owned and shared-with-me rows; `includeArchived` adds the owner's ARCHIVED ones. */
  async list(opts: { includeArchived?: boolean } = {}): Promise<VirtualAccountListItem[]> {
    const wire = opts.includeArchived
      ? await request<WireListItem[]>('GET', '/v1/virtual-accounts', {
          query: { includeArchived: true },
        })
      : await request<WireListItem[]>('GET', '/v1/virtual-accounts');
    return wire.map(normalizeListItem);
  },
  /** GET /v1/virtual-accounts/{id} — a 404 never says whether it is absent or unshared (MM-D-05). */
  async get(virtualAccountId: number): Promise<VirtualAccountDetail> {
    return normalizeDetail(
      await request<WireDetail>('GET', `/v1/virtual-accounts/${virtualAccountId}`),
    );
  },
  /** POST /v1/virtual-accounts. */
  async create(body: VirtualAccountCreateRequest): Promise<VirtualAccountDetail> {
    return normalizeDetail(
      await request<WireDetail>('POST', '/v1/virtual-accounts', { json: body }),
    );
  },
  /**
   * PATCH /v1/virtual-accounts/{id} — rename (the owner or a Manager, MM-D-115) and/or reorder. Sends
   * only the keys given; an omitted key is unchanged.
   */
  async update(
    virtualAccountId: number,
    body: VirtualAccountPatchRequest,
  ): Promise<VirtualAccountDetail> {
    return normalizeDetail(
      await request<WireDetail>('PATCH', `/v1/virtual-accounts/${virtualAccountId}`, {
        json: body,
      }),
    );
  },
  /** POST /v1/virtual-accounts/{id}/archive — envelopes stay inside; sharers lose sight of it (MM-D-62). */
  async archive(virtualAccountId: number): Promise<void> {
    await request<void>('POST', `/v1/virtual-accounts/${virtualAccountId}/archive`);
  },
  /** POST /v1/virtual-accounts/{id}/restore. */
  async restore(virtualAccountId: number): Promise<void> {
    await request<void>('POST', `/v1/virtual-accounts/${virtualAccountId}/restore`);
  },
  /** POST /v1/me/virtual-accounts/reorder — the caller's own ACTIVE virtual accounts (TMMAPP-0096). */
  async reorder(virtualAccountIds: number[]): Promise<VirtualAccountListItem[]> {
    const wire = await request<WireListItem[]>('POST', '/v1/me/virtual-accounts/reorder', {
      json: { virtualAccountIds },
    });
    return wire.map(normalizeListItem);
  },
  /** GET /v1/virtual-accounts/{id}/export.xlsx — the P&L workbook; the server resolves the default window. */
  async exportXlsx(
    virtualAccountId: number,
    window: { from?: string; to?: string } = {},
  ): Promise<DownloadResult> {
    return download(`/v1/virtual-accounts/${virtualAccountId}/export.xlsx`, {
      query: { from: window.from, to: window.to },
    });
  },
};
